// Formatação do WhatsApp para a prévia: *negrito*, _itálico_, ~tachado~, ```monoespaçado```, `código`,
// listas ("* ", "- ", "1. ") e citação ("> "). Devolve uma árvore de nós; quem desenha é o WhatsappText.
//
// Blocos (por linha): { type: 'line' | 'quote', children } | { type: 'bullet' | 'numbered', items: [{ number?, children }] }
// Trechos (dentro da linha): { type: 'text', text } | { type: 'bold' | 'italic' | 'strike', children }
//                            | { type: 'mono' | 'code', text }

const STYLES = { '*': 'bold', '_': 'italic', '~': 'strike' };
const MONO = '```';

const isSpace = (char) => /\s/.test(char);
const isWordChar = (char) => /[\p{L}\p{N}]/u.test(char);

// Abre só no começo, depois de espaço ou de pontuação/símbolo (não no meio de palavra: "a*b*c")
const canOpen = (text, index) => (index === 0) || !isWordChar(text[index - 1]);

// Fecha só no fim, antes de espaço ou de pontuação/símbolo
const canClose = (text, index) => (index === text.length - 1) || !isWordChar(text[index + 1]);

// Primeiro marcador igual que fecha: sem espaço antes dele e sem letra/número depois
function findClose(text, open, marker) {
  for (let j = open + 2; j < text.length; j++) {
    if ((text[j] === marker) && !isSpace(text[j - 1]) && canClose(text, j)) return j;
  }
  return -1;
}

// Trechos de um pedaço de linha (sem \n e sem ```). Código não tem formatação dentro.
function parseInline(text) {
  const nodes = [];
  let buffer = '';

  const flush = () => {
    if (buffer) nodes.push({ type: 'text', text: buffer });
    buffer = '';
  };

  let i = 0;
  while (i < text.length) {
    const char = text[i];

    if (char === '`') {
      const close = text.indexOf('`', i + 1);
      if (close > i + 1) {
        flush();
        nodes.push({ type: 'code', text: text.slice(i + 1, close) });
        i = close + 1;
        continue;
      }
    }

    if (STYLES[char] && canOpen(text, i) && (i + 1 < text.length) && !isSpace(text[i + 1]) && (text[i + 1] !== char)) {
      const close = findClose(text, i, char);
      if (close !== -1) {
        flush();
        nodes.push({ type: STYLES[char], children: parseInline(text.slice(i + 1, close)) });
        i = close + 1;
        continue;
      }
    }

    buffer += char;
    i++;
  }

  flush();
  return nodes;
}

// Separa os ```monoespaçados``` (podem ter várias linhas) do resto do texto
function splitMono(text) {
  const parts = [];
  let rest = text;

  while (rest) {
    const open = rest.indexOf(MONO);
    const close = (open === -1) ? -1 : rest.indexOf(MONO, open + MONO.length);

    if ((open === -1) || (close === -1)) {
      parts.push(rest);
      break;
    }

    if (close === open + MONO.length) {
      // `````` vazio: não é monoespaçado
      parts.push(rest.slice(0, close));
      rest = rest.slice(close);
      continue;
    }

    if (open > 0) parts.push(rest.slice(0, open));
    parts.push({ type: 'mono', text: rest.slice(open + MONO.length, close) });
    rest = rest.slice(close + MONO.length);
  }

  return parts;
}

// Junta os pedaços em linhas: o \n de texto comum quebra a linha; o de dentro do ``` não
function splitLines(parts) {
  const lines = [[]];

  for (const part of parts) {
    if (typeof part !== 'string') {
      lines[lines.length - 1].push(part);
      continue;
    }

    part.split(/\r?\n/).forEach((piece, index) => {
      if (index > 0) lines.push([]);
      if (piece) lines[lines.length - 1].push(piece);
    });
  }

  return lines;
}

const BULLET = /^[*-] /;
const NUMBERED = /^(\d+)\. /;
const QUOTE = /^> ?/;

// Tira o marcador do começo da linha (só se a linha começa com texto comum)
function stripPrefix(line, regex) {
  if (typeof line[0] !== 'string') return null;
  const match = line[0].match(regex);
  if (!match) return null;

  const first = line[0].slice(match[0].length);
  return { match, parts: first ? [first, ...line.slice(1)] : line.slice(1) };
}

const parseParts = (parts) => parts.flatMap((part) => (typeof part === 'string') ? parseInline(part) : [part]);

export function parseWhatsapp(text) {
  const blocks = [];

  for (const line of splitLines(splitMono(text || ''))) {
    const last = blocks[blocks.length - 1];
    let found;

    if ((found = stripPrefix(line, BULLET))) {
      const item = { children: parseParts(found.parts) };
      if (last?.type === 'bullet') last.items.push(item);
      else blocks.push({ type: 'bullet', items: [item] });
      continue;
    }

    if ((found = stripPrefix(line, NUMBERED))) {
      const item = { number: Number(found.match[1]), children: parseParts(found.parts) };
      if (last?.type === 'numbered') last.items.push(item);
      else blocks.push({ type: 'numbered', items: [item] });
      continue;
    }

    if ((found = stripPrefix(line, QUOTE))) {
      blocks.push({ type: 'quote', children: parseParts(found.parts) });
      continue;
    }

    blocks.push({ type: 'line', children: parseParts(line) });
  }

  return blocks;
}
